import { defineStore } from 'pinia'
import countryToCurrency from 'country-to-currency'
import uuid from 'uuid-random'

import api from '@/api'
import config from '../config'
import Jet from '@/modules/Jet'
import Sentry from '@/modules/Sentry'
import { getAnalytic } from '@/plugins/Amplitude'
import { getAppboy } from '@/plugins/Braze'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE } from '@/helpers/environment'
import {
	SUPPORTED_CURRENCIES,
	ZERO_DECIMAL_CURRENCIES,
	GDPR_COUNTRIES,
	EU_REGION_COUNTRIES_REGEX,
} from '@/helpers/constants'

import request, {
	marketingRequest,
	paymentApiAxiosInstance as paymentApi,
	subscriptionApiAxiosInstance as subscriptionApi,
} from '@/helpers/request'
import { bmiCalculator } from '@/helpers/userData'
import { filterInvalidKeys } from '@/helpers/utils'

import { useSurveyStore } from './survey'
import { useLocationStore } from './location'
import { useStatusStore } from './status'
import { useSubscriptionStore } from './subscription'
import { usePaltaStore } from './palta'
import { useUserFlagsStore } from './userFlags'
import { usePaywallStore } from './paywall'
import { weeklyStorage, permanentStorage, sessionOrPreviewStorage, sharedKeyPersistFlat } from './persistence'

const FORCE_EUR_COUNTRIES = ['BG', 'CZ', 'HR', 'HU', 'PL', 'RO']

export const useMainStore = defineStore('main', {
	state: () => ({
		auth: {
			token: null,
			refreshToken: null,
			expireAt: null,
		},
		auth0: {
			accessToken: null,
			idToken: null,
			expiresAt: null,
		},
		absmartly: {
			exposures: {},
		},
		abSmartlyReady: false, // NOT persisted — resets to false on each page load
		notification: null,
		email: '',
		name: '',
		zip: '',
		lastEmailSendAt: null,

		user: null,
		main_user_id: null,

		currency: 'usd',
		upsaled: false,
		hasWorkbookPurchaseError: false,
		geo: null,
		marketingDeduplicationEventId: uuid(),
		appTheme: null,
		organicTrafficOptions: {
			paymentConsent: false,
			paymentConsentError: false,
			upgradeConsent: false,
			upgradeConsentError: false,
		},
		loginCredentials: {
			email: null,
			code: null,
			sentAt: null,
			attempts: 0,
			mode: 'default',
		},
		hasPrevSurvey: false,
		signedIn: false,
		languageModal: {
			showModal: false,
			screenName: '',
		},
	}),

	getters: {
		getAuthCredentials(state) {
			return state.auth
		},

		getAuth0Credentials(state) {
			return state.auth0
		},

		isAnonymous(state) {
			return state.user && state.user.isAnonymous
		},

		isAuthenticated(state) {
			return state.user && state.user.isThirdParty
		},

		isOnboarded(state) {
			return state.user && state.user.isOnboarded
		},

		getMeasurementSystem: () => (type) => {
			return ['lbs', 'ft'].includes(type) ? 'imperial' : 'metric'
		},

		getHeightUnitSystem() {
			const surveyStore = useSurveyStore()
			let value = surveyStore.getPropertyValue('measurement_system_height', true)
			if (!value) {
				if (this.getCountryCode === 'us') {
					value = 'ft'
				} else {
					value = 'cm'
				}
			}
			return value
		},

		getWeightUnitSystem() {
			const surveyStore = useSurveyStore()
			let value = surveyStore.getPropertyValue('measurement_system_weight', true)
			if (Array.isArray(value)) {
				value = value.toString()
			}

			if (!value) {
				const heightUnitSystem = surveyStore.getPropertyValue('measurement_system_height', true)

				if (heightUnitSystem === 'ft') {
					return 'lbs'
				} else if (heightUnitSystem === 'cm') {
					return 'kg'
				}

				if (this.getCountryCode === 'us') {
					return 'lbs'
				} else if (this.getCountryCode === 'gb') {
					return 'st'
				} else {
					return 'kg'
				}
			}

			return value
		},

		getBmi() {
			const surveyStore = useSurveyStore()
			const weight = surveyStore.getPropertyValue('weight', false)
			const height = surveyStore.getPropertyValue('height', false)
			if (!weight || !height) {
				return null
			}
			return bmiCalculator(weight, height)
		},

		getUserId(state) {
			return state.main_user_id || state.user?.id
		},

		hasAlreadyExistedAccount(state) {
			return state.main_user_id && state.user?.id && state.main_user_id !== state.user?.id
		},

		getUserEmail(state) {
			return state.email || state.user?.email
		},

		getUserName(state) {
			if (state.user?.name === 'Anonymous') {
				return ''
			}

			return state.user?.name ?? ''
		},

		getRegionCode(state) {
			return state.geo?.region
		},

		getCountryCode(state) {
			return state.geo?.countryCode
		},

		getCountryCodeUppercase(state) {
			return state.geo?.countryCode?.toUpperCase()
		},

		getGeo(state) {
			return state.geo ?? {}
		},

		getGdprStatus(state) {
			return state.geo?.gdpr
		},

		getIsEUCountry(state) {
			return state.geo?.isEUCountry
		},

		getIsRoWCountry() {
			return !['US', 'CA', 'GB', 'AU'].includes(this.getCountryCodeUppercase) && !this.getIsEUCountry
		},

		getIsLatamCountry() {
			return ['MX', 'BR', 'AR', 'CO', 'CL', 'EC', 'UY', 'PE', 'CR', 'PA', 'BO', 'SV', 'PY', 'VE'].includes(
				this.getCountryCodeUppercase,
			)
		},

		getCurrency() {
			const subsStore = useSubscriptionStore()
			return subsStore.getCurrency || this.currency
		},

		getCurrencyUppercase() {
			return this.getCurrency?.toUpperCase?.()
		},

		getIsZeroDecimalCurrency() {
			return ZERO_DECIMAL_CURRENCIES.includes(this.getCurrency)
		},

		isAbSmartlyReady(state) {
			return state.abSmartlyReady
		},
		getAbSmartlyExposures(state) {
			return Object.values(state.absmartly.exposures)
		},
		getAppTheme(state) {
			return state.appTheme ?? 'light'
		},
		getOrganicTrafficOptions(state) {
			return state.organicTrafficOptions
		},
		isSignedIn(state) {
			return state.signedIn
		},
		isPreviewMode() {
			if (!ENV_DEV_OR_STAGING) {
				return false
			}
			const locationStore = useLocationStore()
			return Boolean(locationStore.getQueryParam('is_ob_preview'))
		},
		getLanguageModalState(state) {
			return state.languageModal
		},
	},

	actions: {
		// Former mutations
		setUser(user) {
			this.user = user
		},

		setAuthCredentials({ token, refreshToken, expireAt }) {
			this.auth.token = token
			this.auth.refreshToken = refreshToken
			this.auth.expireAt = expireAt
		},

		setAuth0Credentials({ accessToken, idToken, expiresAt }) {
			this.auth0.accessToken = accessToken
			this.auth0.idToken = idToken
			this.auth0.expiresAt = expiresAt
		},

		setAbSmartlyReady() {
			this.abSmartlyReady = true
		},

		addAbSmartlyExposure(exposure) {
			const { name, iteration, variantLetter, variantName } = exposure
			const key = `${name}_${iteration}`
			const exposureData = { name, iteration, variantLetter, variantName }

			this.absmartly.exposures[key] = exposureData
		},

		removeAbSmartlyExposure({ name, iteration }) {
			delete this.absmartly.exposures[`${name}_${iteration}`]
		},

		markOnboarded() {
			if (this.user) {
				this.user.isOnboarded = true
			}
		},

		resetNotification() {
			this.notification = null
		},

		setNotification(message) {
			this.notification = message
		},

		saveEmail(value) {
			this.email = value
		},

		saveName(value) {
			this.name = value
		},

		saveZip(value) {
			this.zip = value
		},

		updateLastEmailSendAt(value) {
			this.lastEmailSendAt = value
		},

		setCurrency(currency) {
			this.currency = currency
		},

		setUpsaled(upsaled) {
			this.upsaled = upsaled
		},

		setMainUserId(user_id) {
			this.main_user_id = user_id
		},

		setWorkbookPurchaseError(value) {
			this.hasWorkbookPurchaseError = value
		},

		setGeo(geo) {
			this.geo = geo
		},

		setAppTheme(theme) {
			this.appTheme = theme
		},

		setOrganicTrafficOption({ key, value } = {}) {
			this.organicTrafficOptions[key] = value
		},

		setLoginCredentials(data) {
			this.loginCredentials = data
		},

		setHasPrevSurvey(value) {
			this.hasPrevSurvey = value
		},

		setSignedIn() {
			this.signedIn = true
		},

		setLanguageModalState({ showModal, screenName }) {
			this.languageModal.showModal = showModal
			this.languageModal.screenName = screenName || ''
		},

		// Former actions
		async setUserData(user) {
			const surveyStore = useSurveyStore()
			const locationStore = useLocationStore()
			this.user = user

			let userId = user.id

			const isCrmAutologin = locationStore.isCrmAutologin
			const isInAppAutologin = locationStore.isInAppAutologin
			const isCallAutologin = locationStore.isCallAutologin

			if (isCrmAutologin || isInAppAutologin || isCallAutologin) {
				const userIdFromQueryParam = locationStore.getQueryParam('user')
				const userEmailFromQueryParam = locationStore.getQueryParam('email')
				const userNameFromQueryParam = locationStore.getQueryParam('userName')
				const userHeightFromQueryParam = locationStore.getQueryParam('userHeight')
				const userWeightFromQueryParam = locationStore.getQueryParam('userWeight')
				const userTargetWeightFromQueryParam = locationStore.getQueryParam('userTargetWeight')
				const userAgeFromQueryParam = locationStore.getQueryParam('userAge')
				const userSexFromQueryParam = locationStore.getQueryParam('userSex')

				if (uuid.test(userIdFromQueryParam)) {
					userId = userIdFromQueryParam
					this.main_user_id = userIdFromQueryParam
				}

				if (userEmailFromQueryParam) {
					this.email = userEmailFromQueryParam
				}

				if (userNameFromQueryParam) {
					this.name = userNameFromQueryParam
				}

				if (userHeightFromQueryParam) {
					surveyStore.updateProperty({ property: 'height', value: parseInt(userHeightFromQueryParam) })
				}

				if (userWeightFromQueryParam) {
					surveyStore.updateProperty({ property: 'weight', value: parseInt(userWeightFromQueryParam) })
				}

				if (userTargetWeightFromQueryParam) {
					surveyStore.updateProperty({
						property: 'targetWeight',
						value: parseInt(userTargetWeightFromQueryParam),
					})
				}

				if (userAgeFromQueryParam) {
					surveyStore.updateProperty({
						property: 'age',
						value: parseInt(userAgeFromQueryParam),
						isData: true,
					})
				}

				if (userSexFromQueryParam) {
					surveyStore.updateProperty({ property: 'sex', value: userSexFromQueryParam })
				}
			}

			getAnalytic().setUserId(userId)
			Jet.setUser({ userId: userId })
			window.dataLayer?.push({ user_id: userId })
			Sentry.setUser()
		},

		setupPreviewUser() {
			const surveyStore = useSurveyStore()
			const userFlagsStore = useUserFlagsStore()
			const mockUser = {
				id: 'preview-user-12345',
				email: 'test@test.test',
				name: 'Preview User',
				isThirdParty: true,
				isOnboarded: true,
				isAnonymous: false,
				weight: 70,
				height: 170,
				targetWeight: 65,
				age: 30,
				sex: 'female',
				data: JSON.stringify({
					main_goal: ['lose_weight'],
					current_priorities: ['healthy_eating'],
					focus_areas: ['core'],
					measurement_system_height: 'cm',
					measurement_system_weight: 'kg',
					workout_level: ['beginner'],
					work: ['office_work'],
				}),
			}

			this.user = mockUser
			this.email = mockUser.email
			this.name = mockUser.name

			surveyStore.updateProperty({ property: 'sex', value: mockUser.sex })
			surveyStore.updateProperty({ property: 'weight', value: mockUser.weight })
			surveyStore.updateProperty({ property: 'height', value: mockUser.height })
			surveyStore.updateProperty({ property: 'targetWeight', value: mockUser.targetWeight })
			surveyStore.updateProperty({ property: 'age', value: mockUser.age, isData: true })

			userFlagsStore.updateLocalFlags({
				freebieAccess: false,
			})

			this.signedIn = true
		},

		async fetchUserFromAws({ includeExpired = false } = {}) {
			const statusStore = useStatusStore()
			if (this.isPreviewMode) {
				this.setupPreviewUser()
				return
			}
			try {
				if (statusStore.resourceIsPending('user')) {
					return
				}

				statusStore.setResourcePending('user')

				const response = await api.getUser()

				await this.setUserData(response.data)
				await this.loadSubscription({ includeExpired })

				if (response.data.data) this.mergeUserData()
			} catch (e) {
				getAnalytic().logEvent('Get user error', { e })
			} finally {
				statusStore.unsetResourcePending('user')
			}
		},

		async loadSubscription({ retry = false, retryNumber = 0, includeExpired = false } = {}) {
			const subsStore = useSubscriptionStore()
			let isError = false

			if (retry) {
				retryNumber += 1
			}

			try {
				const userId = this.getUserId
				await subsStore.fetchSubscription({
					customer_id: userId,
					includeExpired: includeExpired,
				})

				if (!subsStore.hasSubscription) {
					if (retry && retryNumber < 3) {
						await new Promise((resolve) => setTimeout(resolve, 500 * retryNumber))
						return this.loadSubscription({ retry: true, retryNumber })
					}
				}
			} catch {
				isError = true

				if (retryNumber < 3) {
					await new Promise((resolve) => setTimeout(resolve, 500 * retryNumber))
					return this.loadSubscription({ retry: true, retryNumber })
				}
			} finally {
				const subscription = subsStore.getSubscription

				getAnalytic().logEvent('WebGetActiveSubscription', {
					error: isError,
					retry_number: retryNumber,
					price_point_id: subscription?.product_id ?? 'none',
					subscription_id: subscription?.subscription_id ?? 'none',
				})
			}
		},

		switchPaltaSubscription(params) {
			const paltaStore = usePaltaStore()
			return paltaStore.switchSubscription(params)
		},

		makePaltaPurchase(params) {
			const paltaStore = usePaltaStore()
			return paltaStore.purchase(params).then((result) => {
				return result.data
			})
		},

		async checkExistingUserByEmail(params) {
			const response = await api.userCheck(params)
			const userId = response.data

			this.main_user_id = userId
			Sentry.setUser()

			await this.loadSubscription()

			return userId
		},

		async sendConfirmEmail(params) {
			await api.sendConfirmEmail({
				email: this.email,
				...params,
			})
			this.lastEmailSendAt = new Date()
		},

		async createPaymentCustomer() {
			let email = this.getUserEmail
			let name = this.getUserName
			let user_id = this.getUserId
			let abSmartlyExposures = this.getAbSmartlyExposures

			await fetch(`${config('SimpleAPIUrl')}v1/api/customers`, {
				method: 'POST',
				body: JSON.stringify({
					user_id,
					email,
					name,
					experiments: abSmartlyExposures,
				}),
			}).catch((error) => {
				console.error(error.response)
			})
		},

		async fetchPaymentCustomer() {
			const paywallStore = usePaywallStore()
			return await subscriptionApi
				.get(`billing/v1/customers/${this.getUserId}`)
				.then((response) => {
					const paymentMethod = response?.data?.payment_method
					if (paymentMethod) {
						paywallStore.setLastUsedProvider(paymentMethod.provider)
					}
					return response?.data
				})
				.catch(() => null)
		},

		/**
		 * Method for sending marketing campaign data to Payment API
		 * @param {object} data
		 * @param {'facebook'|'google'|'impact'|'twitter'} data.source - source of campaign
		 * @param {object} data.params
		 */
		sendMarketingCampaignData(data) {
			const { source, params } = data

			return fetch(`${config('SimpleAPIUrl')}v1/api/campaign-tracker/${source}`, {
				method: 'POST',
				body: JSON.stringify(params),
			})
				.then(() => {
					if (ENV_DEV_OR_STAGING) {
						/* eslint-disable no-console */
						console.groupCollapsed(
							`%cMarketing campaign tracker [debug]: %c${source}`,
							'font-weight: 400',
							'color: #19ffb2',
						)
						console.log(data)
						console.groupEnd()
						/* eslint-enable no-console */
					}
				})
				.catch((error) => {
					Sentry.withScope((scope) => {
						scope.setExtras({ source, ...params })
						scope.setTags({
							marketingCampaignTrackerError: true,
						})
						scope.captureException(error)
					})
				})
		},

		async createMeasurement() {
			const userWeight = this.user.weight

			if (typeof userWeight !== 'number' || userWeight <= 0) {
				Sentry.withScope((scope) => {
					scope.setExtras({ user: this.user, userWeight })
					scope.setTags({
						emailSavingError: true,
						emailSavingErrorType: 'incorrect_measurements',
					})
					scope.captureMessage('Incorrect weight at user/measurements')
				})
			}

			return await api.createMeasurement({
				records: [
					{
						type: 'Weight',
						value: userWeight,
						source: 'Manual',
						date: new Date(),
						secondsFromGMT: -new Date().getTimezoneOffset() * 60,
					},
				],
			})
		},

		/**
		 * Get country code and currency from https://assets.simple.org/geo.json
		 * @returns {Promise<{countryCode: String, currency: String}>}
		 */
		async fetchSimpleGeo({ countryOverride, regionOverride }) {
			return request(config('GeoAPIUrl')).then((response = {}) => {
				let { city, continent, country, isEUCountry, region, regionCode, postalCode } = response

				if (countryOverride) {
					country = countryOverride
				}

				if (regionOverride) {
					regionCode = regionOverride
				}

				let currency = countryToCurrency[country] ?? 'USD'
				if (FORCE_EUR_COUNTRIES.includes(country)) {
					currency = 'EUR'
				}

				const result = {
					countryCode: country?.toLowerCase() ?? 'us',
					currency: currency,
					continentCode: continent,
					city: city?.toLowerCase(),
					isEUCountry: isEUCountry === '1',
					regionName: region?.toLowerCase(),
					region: regionCode?.toLowerCase(),
					zip: postalCode,
				}

				return filterInvalidKeys(result)
			})
		},

		async fetchIpWhoIsGeo() {
			return request('https://ipwho.is/')
		},

		/**
		 * Fetch geo data and set it to the store
		 * @returns {Promise<void>}
		 */
		async fetchGeo({ country, region }) {
			const locationStore = useLocationStore()
			let currencyCode = 'usd'

			if (this.geo && !country) {
				return
			}

			if (ENV_DEV_OR_STAGING || ENV_HACKERONE) {
				const queryParamCountry = country || locationStore.getQueryParam('country')
				if (queryParamCountry && /^[A-Z]{2}$/.test(queryParamCountry)) {
					country = queryParamCountry
					region = region || locationStore.getQueryParam('region')

					this.currency = currencyCode

					let currency = countryToCurrency[country] ?? 'USD'
					if (FORCE_EUR_COUNTRIES.includes(country)) {
						currency = 'EUR'
					}

					this.geo = {
						countryCode: country.toLowerCase(),
						region: region,
						currency: currency,
						gdpr: GDPR_COUNTRIES.includes(country),
						isEUCountry: EU_REGION_COUNTRIES_REGEX.test(country),
					}
				}
			}

			const simpleGeoResult = await this.fetchSimpleGeo({
				countryOverride: country,
				regionOverride: region,
			})

			if (!simpleGeoResult) {
				return
			}

			if (SUPPORTED_CURRENCIES.includes(simpleGeoResult.currency)) {
				currencyCode = simpleGeoResult.currency?.toLowerCase()
			}

			this.currency = currencyCode
			this.geo = {
				...simpleGeoResult,
				gdpr: GDPR_COUNTRIES.includes(simpleGeoResult.countryCode?.toUpperCase()),
				// isEUCountry comes from fetchSimpleGeo (API: isEUCountry === '1'), preserved via spread
			}
		},

		async fetchUserLocalTax() {
			const locationStore = useLocationStore()
			const hasRequiredGeoFields = this.geo && ['countryCode', 'currency'].every((key) => key in this.geo)

			if (!hasRequiredGeoFields) {
				return Promise.resolve(null)
			}

			const params = {
				user_id: this.getUserId,
				geo_data: this.geo,
			}

			return await fetch(`${config('SimpleAPIUrl')}v1/api/geodata`, {
				method: 'POST',
				body: JSON.stringify(params),
			})
				.then((response) => response.json())
				.catch((error) => {
					Sentry.withScope((scope) => {
						scope.setExtras({ params, queryParams: locationStore.queryParams })
						scope.setTags({
							paymentApiError: true,
						})
						scope.captureException(error)
					})
				})
		},

		logout() {
			localStorage.clear()
			window.location.href = '/'
		},

		mergeUserData() {
			const surveyStore = useSurveyStore()
			let user = this.user
			let data = user.data ? JSON.parse(user.data) : {}

			Object.keys(this.user)
				.filter((key) => key in surveyStore.values)
				.forEach((key) => {
					if (key == 'data') {
						Object.keys(data)
							.filter((data_key) => data_key in surveyStore.values.data)
							.forEach((data_key) => {
								surveyStore.values.data[data_key] = data[data_key] === 'null' ? null : data[data_key]
							})
					} else {
						const surveyValues = surveyStore.values[key]
						const userValues = this.user[key]

						if (Array.isArray(surveyValues) && Array.isArray(userValues)) {
							const uniqueValues = Array.from(new Set([...surveyValues, ...userValues]))
							surveyStore.values[key] = uniqueValues
						} else {
							surveyStore.values[key] = userValues
						}
					}
				})

			const msHeight = surveyStore.values.data.measurement_system_height
			const msWeight = surveyStore.values.data.measurement_system_weight
			if (surveyStore.values.data.measurement_system && (!msHeight || !msWeight)) {
				const isMetric = surveyStore.values.data.measurement_system?.toString() === 'metric'
				surveyStore.values.data.measurement_system_height = msHeight || (isMetric ? 'cm' : 'ft')
				surveyStore.values.data.measurement_system_weight = msWeight || (isMetric ? 'kg' : 'lbs')
			}
		},

		async activateFreebieAccess(freebieCode) {
			return await paymentApi.post(`v1/api/free-access/activate`, {
				code: freebieCode,
				user_id: this.getUserId,
			})
		},

		async sendReferralCode(referralCode) {
			return await subscriptionApi.post(`/billing/v1/customer/${this.getUserId}/partners`, {
				referral_code: referralCode,
			})
		},

		sendAbSmartlyExposuresToBraze(exposures) {
			if (!exposures) {
				exposures = this.absmartly.exposures
			}

			for (let exposureName in exposures) {
				const { name, iteration, variantLetter, variantName } = exposures[exposureName]
				const underscoredName = name.replaceAll(' ', '_')

				getAppboy().logCustomEvent('Experiment - Exposure', {
					Name: name,
					Iteration: `i${iteration}`,
					Variant: variantLetter,
					VariantName: variantName,
				})
				getAppboy().addToCustomAttributeArray(
					'active_test_groups_web',
					`${underscoredName}(${variantLetter})(${`i${iteration}`})`,
				)
			}
		},

		async updateCustomerInfo(options) {
			return await paymentApi.patch(`v1/customers/${this.getUserId}`, { ...options })
		},

		async saveUserMarketingParams(params) {
			try {
				await marketingRequest('/actions/user_click', {
					method: 'POST',
					data: params,
				})
			} catch {
				// Just let the user pass anyway
			}
		},

		async sendMarketingPurchaseTrackEvent() {
			await marketingRequest('actions/purchase_track/frontend', {
				method: 'put',
				params: {
					user_id: this.getUserId,
				},
			})
		},

		sendRenewalReminder(params) {
			return subscriptionApi.post(`/billing/v1/subscription/reminder`, {
				subscription_id: params.subscriptionId,
				has_reminder: params.hasReminder,
			})
		},

		getRenewalReminder(params) {
			return subscriptionApi.get(`/billing/v1/subscription/${params.subscriptionId}/reminder`)
		},
	},

	persist: [
		sharedKeyPersistFlat('vuex', weeklyStorage, [
			'absmartly',
			'marketingDeduplicationEventId',
			'email',
			'main_user_id',
		]),
		sharedKeyPersistFlat('auth', permanentStorage, ['auth', 'auth0']),
		// email intentionally persisted in BOTH weekly localStorage AND sessionStorage:
		// E2E tests write email to sessionStorage, and old Vuex read from both storages implicitly.
		// Pinia reads only from its configured storage, so we persist here to keep test compat.
		sharedKeyPersistFlat('vuex', sessionOrPreviewStorage, [
			'email',
			'geo',
			'name',
			'zip',
			'upsaled',
			'lastEmailSendAt',
			'currency',
			'organicTrafficOptions',
			'loginCredentials',
			'hasPrevSurvey',
			'signedIn',
		]),
	],
})
